import React, { useEffect, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_API_URL = process.env.REACT_APP_API_URL;

const SocialMediaAutomation = () => {
  const defaultForm = {
    topic: "",
    keywords: "",
    tone: "",
    audience: "",
    platforms: "", // string now
  };

  const defaultPosts = {
    linkedin: "",
    facebook: "",
    instagram: "",
    imageUrl: "",
  };

  const [formData, setFormData] = useState(defaultForm);
  const [generatedPosts, setGeneratedPosts] = useState(defaultPosts);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const tones = ["Professional", "Friendly", "Promotional", "Motivational"];
  const platformList = ["LinkedIn", "Facebook", "Instagram"];

  const [futurePosts, setFuturePosts] = useState([]);

  const loadFuturePosts = async () => {
    try {
      const response = await axios.get(`${BASE_API_URL}/social/future-posts`);

      setFuturePosts(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadFuturePosts();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePlatformChange = (platform) => {
    let currentPlatforms = formData.platforms
      ? formData.platforms.split(",")
      : [];

    const exists = currentPlatforms.includes(platform);

    let updatedPlatforms;

    if (exists) {
      updatedPlatforms = currentPlatforms.filter((p) => p !== platform);
    } else {
      updatedPlatforms = [...currentPlatforms, platform];
    }

    setFormData({
      ...formData,
      platforms: updatedPlatforms.join(","), // convert array to string
    });
  };

  /* Generate Instant Content */
  const handleGenerate = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${BASE_API_URL}/social/generate`,
        formData,
      );

      setGeneratedPosts(response.data);

      toast.success("Content generated successfully");
    } catch (error) {
      toast.error("Failed to generate content");
    } finally {
      setLoading(false);
    }
  };

  /* Save for Future Post */
  const handleSaveFuturePost = async () => {
    try {
      setSaving(true);

      await axios.post(`${BASE_API_URL}/social/save-future-post`, formData);

      toast.success("Topic saved for future scheduled posting");

      setFormData(defaultForm);

      loadFuturePosts();
    } catch (error) {
      toast.error("Failed to save future post");
    } finally {
      setSaving(false);
    }
  };

  /* Publish Now */
  const handlePublish = async () => {
    try {
      setPublishing(true);

      await axios.post(`${BASE_API_URL}/social/publish`, generatedPosts);

      toast.success("Published successfully! Please check your account.");

      setGeneratedPosts(defaultPosts);
      setFormData(defaultForm);
    } catch (error) {
      const message = error.response?.data || error.message || "Publish failed";

      toast.error(message);
      // Note: generatedPosts and formData are intentionally left untouched
      // so the user doesn't lose their content and can retry.
    } finally {
      setPublishing(false);
    }
  };

  return (
    <DashboardLayout>
      <ToastContainer position="top-right" autoClose={2500} />

      <div
        className="container-fluid p-4"
        style={{ backgroundColor: "#F7F8FA", minHeight: "100vh" }}
      >
        {/* Header */}
        <div className="mb-4">
          <h2
            className="fw-bold mb-1"
            style={{ color: "#14213D", letterSpacing: "-0.01em" }}
          >
            Social Media Automation
          </h2>
          <p className="text-muted mb-0">
            Generate instant content or schedule future AI posts
          </p>
        </div>

        {/* ========================= */}
        {/* ROW 1 => 2 COLUMNS */}
        {/* LEFT = FORM */}
        {/* RIGHT = FUTURE TOPICS */}
        {/* ========================= */}
        <div className="row g-4 mb-4">
          {/* FORM SECTION */}
          <div className="col-lg-6">
            <div
              className="card shadow border-0 rounded-4 p-4 h-100"
              style={{ boxShadow: "0 8px 24px rgba(20, 33, 61, 0.08)" }}
              // style={{
              //     maxHeight: "85vh",
              //     overflowY: "auto",
              // }}
            >
              <h5 className="fw-bold mb-3" style={{ color: "#14213D" }}>
                Create New Topic
              </h5>

              {/* Topic */}
              <div className="mb-3">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "14px", color: "#3D4457" }}
                >
                  Topic
                </label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  placeholder="Enter topic"
                />
              </div>

              {/* Keywords */}
              <div className="mb-3">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "14px", color: "#3D4457" }}
                >
                  Keywords
                </label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleChange}
                  placeholder="Hiring, Growth..."
                />
              </div>

              {/* Tone */}
              <div className="mb-3">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "14px", color: "#3D4457" }}
                >
                  Tone
                </label>
                <select
                  className="form-select rounded-3"
                  name="tone"
                  value={formData.tone}
                  onChange={handleChange}
                >
                  <option value="">Select Tone</option>

                  {tones.map((tone, index) => (
                    <option key={index}>{tone}</option>
                  ))}
                </select>
              </div>

              {/* Audience */}
              <div className="mb-3">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "14px", color: "#3D4457" }}
                >
                  Target Audience
                </label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="audience"
                  value={formData.audience}
                  onChange={handleChange}
                  placeholder="HR, Startups..."
                />
              </div>

              {/* Platforms */}
              <div className="mb-4">
                <label
                  className="form-label d-block fw-semibold"
                  style={{ fontSize: "14px", color: "#3D4457" }}
                >
                  Platforms
                </label>

                <div className="d-flex flex-wrap gap-2">
                  {platformList.map((platform, index) => {
                    const isSelected = formData.platforms.includes(platform);
                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => handlePlatformChange(platform)}
                        className="rounded-pill fw-semibold"
                        style={{
                          fontSize: "14px",
                          padding: "8px 18px",
                          border: isSelected
                            ? "1px solid #6D28D9"
                            : "1px solid #E9D5FF",
                          backgroundColor: isSelected ? "#6D28D9" : "#FAF5FF",
                          color: isSelected ? "#FFFFFF" : "#6B21A8",
                          transition:
                            "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
                        }}
                      >
                        {isSelected && <span className="me-1">✓</span>}
                        {platform}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Buttons */}
              <button
                className="btn btn-primary w-100 rounded-pill fw-semibold mb-3"
                onClick={handleGenerate}
                disabled={loading}
                style={{
                  backgroundColor: "#14213D",
                  borderColor: "#14213D",
                  padding: "10px",
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Generating...
                  </>
                ) : (
                  "Generate Instant Content"
                )}
              </button>

              {/* Simple OR */}
              <div className="text-center d-flex align-items-center gap-2 mb-3">
                <hr
                  className="flex-grow-1"
                  style={{ borderTop: "1px solid #E3E6ED" }}
                />
                <small className="text-muted fw-semibold">OR</small>
                <hr
                  className="flex-grow-1"
                  style={{ borderTop: "1px solid #E3E6ED" }}
                />
              </div>

              <button
                className="btn btn-dark w-100 rounded-pill fw-semibold"
                onClick={handleSaveFuturePost}
                disabled={saving}
                style={{ padding: "10px" }}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  "Save For Future Post"
                )}
              </button>
            </div>
          </div>

          {/* FUTURE TOPICS */}
          <div className="col-lg-6">
            <div
              className="card shadow border-0 rounded-4 p-4 h-100"
              style={{ boxShadow: "0 8px 24px rgba(20, 33, 61, 0.08)" }}
            >
              <h5 className="fw-bold mb-3" style={{ color: "#14213D" }}>
                Future Scheduled Topics
              </h5>

              <div
                style={{
                  maxHeight: "75vh",
                  overflowY: "auto",
                }}
              >
                {futurePosts && futurePosts.length > 0 ? (
                  futurePosts.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-3 p-3 mb-3"
                      style={{
                        backgroundColor: "#F7F8FA",
                        border: "1px solid #E3E6ED",
                        borderLeft: "4px solid #14213D",
                      }}
                    >
                      <h6 className="fw-bold mb-1" style={{ color: "#14213D" }}>
                        {item.topic}
                      </h6>

                      <small className="text-muted d-block">
                        {item.keywords}
                      </small>

                      <span
                        className="badge mt-2"
                        style={{
                          backgroundColor: "#FCA311",
                          color: "#14213D",
                          fontWeight: 600,
                          padding: "6px 10px",
                        }}
                      >
                        {item.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted text-center py-4">
                    No future topics available
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* GENERATED CONTENT */}
        <div className="row g-4">
          {/* LinkedIn */}
          <div className="col-lg-6 col-md-6">
            <div
              className="card shadow-sm border-0 rounded-4 p-3 h-100"
              style={{ borderTop: "3px solid #0A66C2" }}
            >
              <h6 className="fw-bold" style={{ color: "#0A66C2" }}>
                LinkedIn
              </h6>
              <p style={{ color: "#3D4457", fontSize: "14px" }}>
                {generatedPosts.linkedin}
              </p>
            </div>
          </div>

          {/* Facebook */}
          <div className="col-lg-6 col-md-6">
            <div
              className="card shadow-sm border-0 rounded-4 p-3 h-100"
              style={{ borderTop: "3px solid #1877F2" }}
            >
              <h6 className="fw-bold" style={{ color: "#1877F2" }}>
                Facebook
              </h6>
              <p style={{ color: "#3D4457", fontSize: "14px" }}>
                {generatedPosts.facebook}
              </p>
            </div>
          </div>

          {/* Instagram */}
          <div className="col-lg-6 col-md-6">
            <div
              className="card shadow-sm border-0 rounded-4 p-3 h-100"
              style={{ borderTop: "3px solid #E1306C" }}
            >
              <h6 className="fw-bold" style={{ color: "#E1306C" }}>
                Instagram
              </h6>
              <p style={{ color: "#3D4457", fontSize: "14px" }}>
                {generatedPosts.instagram}
              </p>
            </div>
          </div>

          {/* Image */}
          <div className="col-lg-6 col-md-6">
            <div className="card shadow-sm border-0 rounded-4 p-3 h-100">
              <h6 className="fw-bold" style={{ color: "#14213D" }}>
                Social Image
              </h6>

              <input
                type="text"
                className="form-control rounded-3 mb-3"
                placeholder="Paste image URL"
                value={generatedPosts.imageUrl}
                onChange={(e) =>
                  setGeneratedPosts({
                    ...generatedPosts,
                    imageUrl: e.target.value,
                  })
                }
              />

              {generatedPosts.imageUrl && (
                <img
                  src={generatedPosts.imageUrl}
                  alt="Preview"
                  className="img-fluid rounded-4"
                  style={{
                    width: "100%",
                    height: "220px",
                    objectFit: "cover",
                    border: "1px solid #E3E6ED",
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Publish */}
        <div className="mt-4">
          <button
            className="btn btn-success rounded-pill px-4 fw-semibold"
            onClick={handlePublish}
            disabled={publishing}
            style={{
              padding: "10px 28px",
              backgroundColor: "#1F9254",
              borderColor: "#1F9254",
            }}
          >
            {publishing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Publishing...
              </>
            ) : (
              "Publish Now"
            )}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SocialMediaAutomation;
